import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect, Route, useHistory } from 'react-router-dom';
import { getAssetDomain } from '../../constants/assets';
import BlockUi from '../blockUi/BlockUi';
import { initializePublicConfiguration } from '../../actions/config';
import Commons from '../../helpers/Commons';
import { routeCodes } from '../../constants/routes';
import { S3_FILTERS } from '../../helpers/Localization';
import {
    saveHeroImage,
    saveLandUseOptions,
    saveDevZoneOptions,
    savePropertySubTypeOptions
} from '../../actions/assets';
import Random from '../../helpers/Random';
import { getUserDetail } from '../../actions/claud';
import { getUserSettings } from '../../actions/userPreferences';
import { userSelectsAccount } from '../../actions/linkedAccounts';
import { LEGACY_URI } from '../../constants/legacyUrls';
import Signature from '../../api/signature';
import cruxProxy from '../../api/cruxProxy';
import UserPrefApi from 'js/api/userPreferences';
import LocalStorageHelper, { PORTAL_SIDEBAR_KEY } from 'js/helpers/LocalStorageHelper';
import Bugsnag from 'js/bugsnag';

const getWebpackBuildHash = () => {
    return process.env.REACT_APP_VERSION;
}

const randomSelect = list =>
    // We used (0xffffffff + 1)/Math.pow(2,-32) because we are using Uint32Array in the
    // Random class We divide the random number by (0xffffffff + 1) to get a value less
    // than 1 but greater than 0 by doing this we ensure that the value returned by Math.floor
    // is within the array's bounds
    list && list[Math.floor((Random.getNumber() / (0xffffffff + 1)) * list.length)]
;

// Portal Sidebar should only open on the first visit on the app.
// But if you just reload the page, it should remain close
const clearPortalSidebarKey = () => LocalStorageHelper.remove(PORTAL_SIDEBAR_KEY);

const CL_APP_ACCT_USR_GUID_PARAM = 'clAppAcctUsrGuid';

const AuthenticatedRoutePing = ({
    isPublicConfigurationInitializedSuccess,
    selectedAccount,
    selectedClAppAccountUserGuid,
    listedAccount,
    isMultipleAccounts,
    tncPermanentlyAccepted,
    tncCurrentlyAccepted,
    postJoinInProgress,
    dispatch,
    ...rest
}) => {
    const history = useHistory();
    const isSignatureCalled = useRef(false);

    const fetchHeroRandomHeroImage = async () => {
        try {
            const jsonFilePath = 'hero-images/hero-images.json';
            const response = await fetch(getAssetDomain(jsonFilePath));
            const heroList = await response.json();
            const formattedAsset = {
                image: randomSelect(heroList.images),
                quote: randomSelect(heroList.quotes),
            };
            dispatch(saveHeroImage(formattedAsset));
        } catch (error) {
            dispatch(saveHeroImage(null));
        }
    }

    const fetchFilterOptions = (filePath, action) => {
        fetch(getAssetDomain(filePath)).then((response) => {
            if (response && response.ok) {
                return response.json()
            }
        }).then((data = []) => {
            dispatch(action(Commons
                .sortStringArrayNumericLast(data.map(String).filter(item => item !== ''))));
        }).catch(error => Bugsnag.notify({
            name: `Unavailable Asset: ${filePath}`,
            message: error.message,
        }));
    }

    useEffect(()=> {
        fetchHeroRandomHeroImage();
        fetchFilterOptions(S3_FILTERS.DEV_ZONE, saveDevZoneOptions);
        fetchFilterOptions(S3_FILTERS.LAND_USE, saveLandUseOptions);
        fetchFilterOptions(S3_FILTERS.PROPERTY_SUB_TYPE, savePropertySubTypeOptions);
        dispatch(getUserSettings());

        if (!isPublicConfigurationInitializedSuccess) {
            console.log('Crux/Init -> AuthenticatedRoutePing -> UseEffect initializePublicConfig');
            dispatch(initializePublicConfiguration(getWebpackBuildHash()));
        }
    }, []);

    //
    // AuthenticatedRoutePing execution logic start:
    //

    if (!listedAccount || listedAccount.length === 0) {
        return (<BlockUi blocking backdropProps={{ style: { height: '100vh' }}} />); // wait until the user's accounts are loaded
    }

    const entireUrl = window.location.href;

    if (entireUrl.includes(routeCodes.JOIN_UP_FORM.path)) {
        return <Route {...rest} />;
    }

    // if post-join is in-progress redirect to JoinUpForm screen
    if (postJoinInProgress) {
        return (
            <Route render={({ location }) => (
                <Redirect
                    to={{
                        pathname: routeCodes.JOIN_UP_FORM.path,
                        state: { from: location },
                    }}
                />)}
            />
        );
    }

    if (entireUrl.endsWith(routeCodes.LINKED_ACCOUNTS.path)) { // on the linkedAccounts screen
        clearPortalSidebarKey();

        // if the account is already selected we need to skip the LinkedAccounts view:
        if (selectedAccount) {

            try {
                UserPrefApi.updateLastAccessedDate(selectedClAppAccountUserGuid);
            } catch (e) {
                console.error(`Error setting the last account accessed time for ${selectedClAppAccountUserGuid}`);
            }

            // dispatch must be done here as we are bypassing LinkedAccounts selection screen, hence claud user details should be dispatched for the pre-selectedAccount
            dispatch(getUserDetail(selectedClAppAccountUserGuid)); // to sync claud.user data to state, bcz launch darkly init is failing otherwise

            if (!tncPermanentlyAccepted && !tncCurrentlyAccepted) {
                return (
                    <Route render={({ location }) => (
                        <Redirect
                            to={{
                                pathname: routeCodes.TERMS_AND_CONDITIONS.path,
                                state: { from: location },
                            }}
                        />)}
                    />
                );
            } else {
                const pathname = cruxProxy.getAndRemoveRequestedPath() || routeCodes.HOME.path;
                history.push(pathname);
                return null;
            }
        }

        return <Route {...rest} />;
    }

    // if no account is selected yet, redirect to the linkedAccounts screen:
    if (!selectedAccount
        && !entireUrl.includes(routeCodes.LINKED_ACCOUNTS.path)
        && !entireUrl.includes(CL_APP_ACCT_USR_GUID_PARAM)) {
            clearPortalSidebarKey();

            return (
                <Route render={({ location }) => (
                    <Redirect
                        to={{
                            pathname: routeCodes.LINKED_ACCOUNTS.path,
                            state: { from: location },
                        }}
                    />)}
                />
            );
    }

    // if the TnC are not permanently or currently accepted yet, redirect to the TnC screen:
    if (!tncPermanentlyAccepted && !tncCurrentlyAccepted && !entireUrl.includes(routeCodes.TERMS_AND_CONDITIONS.path)
        && !entireUrl.includes(CL_APP_ACCT_USR_GUID_PARAM)) {
        clearPortalSidebarKey();

        return (
            <Route render={({ location }) => (
                <Redirect
                    to={{
                        pathname: routeCodes.TERMS_AND_CONDITIONS.path,
                        state: { from: location },
                    }}
                />)}
            />
        );
    }

    // Silent-login & deep-linking crux-ui logic:
    // Currently, the presence of a clAppAcctUsrGuid query param indicates that this is a silent-login URL.
    if (entireUrl.includes(CL_APP_ACCT_USR_GUID_PARAM)) {
        const urlParams = new URLSearchParams(window.location.search);
        const clAppAcctUsrGuid = urlParams.get(CL_APP_ACCT_USR_GUID_PARAM); // here selectedClAppAccountUserGuid retrieved from URL
        let redirectPathUrl = urlParams.get('path');
        let isFromLegacyPdp = false;
        const isLegacy = urlParams.get('legacy');
        if (isLegacy === 'true') {
            redirectPathUrl = decodeURIComponent(redirectPathUrl);

            if (LEGACY_URI.PDP.CRUX_PATH.test(redirectPathUrl) || LEGACY_URI.AVM.CRUX_PATH.test(redirectPathUrl)) {
                isFromLegacyPdp = !!LEGACY_URI.PDP.CRUX_PATH.test(redirectPathUrl);
                redirectPathUrl = Commons.removeRequestParams(redirectPathUrl);
            } else if (redirectPathUrl.includes(LEGACY_URI.SIGNATURE_CMA.CRUX_PATH) && !isSignatureCalled.current) {
                isSignatureCalled.current = true;
                const propertyId = Commons.getRequestParamValue(redirectPathUrl, 'inputPropertyId');
                Signature.getSignatureUrlByType(propertyId, LEGACY_URI.SIGNATURE_CMA.CRUX_PATH, clAppAcctUsrGuid, false);
                redirectPathUrl = '/';
            } else if (redirectPathUrl.includes(LEGACY_URI.SIGNATURE_RENTAL.CRUX_PATH) && !isSignatureCalled.current) {
                isSignatureCalled.current = true;
                const propertyId = Commons.getRequestParamValue(redirectPathUrl, 'inputPropertyId');
                Signature.getSignatureUrlByType(propertyId, LEGACY_URI.SIGNATURE_RENTAL.CRUX_PATH, clAppAcctUsrGuid, false);
                redirectPathUrl = '/';
            } else {
                redirectPathUrl = '/'; //handle second render case
            }
        }

        if (!isFromLegacyPdp) {
            clearPortalSidebarKey();
        }

        if (isMultipleAccounts) {
            const accountDetails = listedAccount.find((listedAccountItem) => listedAccountItem.clAppAccountUserGuid === clAppAcctUsrGuid);
            const clAppAccountGuid = accountDetails.id;
            const selectedAccountPayload = userSelectsAccount(clAppAccountGuid, clAppAcctUsrGuid);
            dispatch(selectedAccountPayload); // dispatching userSelectsAccount call to sync linkedAccount selection
            dispatch(getUserDetail(clAppAcctUsrGuid)); // to sync claud.user data to state
        } else {
            // dispatch must be done here as we are bypassing LinkedAccounts selection screen, hence claud user details should be dispatched for the pre-selectedAccount
            dispatch(getUserDetail(selectedClAppAccountUserGuid)); // to sync claud.user data to state - here selectedClAppAccountUserGuid is from redux store - listedAccount
        }

        if (!tncPermanentlyAccepted && !tncCurrentlyAccepted) {
            return (
                <Route render={({ location }) => (
                    <Redirect
                        to={{
                            pathname: routeCodes.TERMS_AND_CONDITIONS.path,
                            state: {
                                from: location,
                                redirectUrl: redirectPathUrl,
                                silentLogin: true
                            },
                        }}
                    />)}
                />
            );
        } else {
            return (
                <Route render={({ location }) => (
                    <Redirect
                        to={{
                            pathname: redirectPathUrl,
                            state: {
                                from: location,
                            },
                        }}
                    />)}
                />
            );
        }
    }

    if (!selectedAccount) {
        return <BlockUi blocking />;
    }

    return <Route {...rest} />;
};

AuthenticatedRoutePing.propTypes = {
    isPublicConfigurationInitializedSuccess: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    isMultipleAccounts: PropTypes.bool,
};

const selectAuthenticationDetail = ({ config, linkedAccounts }) => ({
    isPublicConfigurationInitializedSuccess: config.get('isPublicConfigurationInitializedSuccess'),
    listedAccount: linkedAccounts.get('listedAccount'),
    selectedAccount: linkedAccounts.get('selectedAccount'),
    selectedClAppAccountUserGuid: linkedAccounts.get('selectedClAppAccountUserGuid'),
    isMultipleAccounts: linkedAccounts.get('listedAccount').length > 1,
    tncPermanentlyAccepted: linkedAccounts.get('tncPermanentlyAccepted'),
    success: linkedAccounts.get('success'),
    tncCurrentlyAccepted: linkedAccounts.get('tncCurrentlyAccepted'),
    postJoinInProgress: linkedAccounts.get('postJoinInProgress'),
});

export default connect(selectAuthenticationDetail)(AuthenticatedRoutePing);
